import React, { Fragment, useEffect, useState } from 'react'
import { connect, useDispatch, useSelector } from 'react-redux'
import ReactGA from 'react-ga4'
import ReactPixel from 'react-facebook-pixel'
import getTheme from './buildTheme'
import moment from 'moment-timezone'
import Routes from './routes/index'
import { BrowserRouter } from 'react-router-dom'
import { createGlobalStyle, ThemeProvider } from 'styled-components'
import useThunkDispatch from 'CustomHooks/useThunkDispatch'
import { useClientTheme } from './hooks/useClientTheme'
import { getUrlParams } from 'CommonComponents'
import { setUrlPrefix } from 'Actions/account_action'
import { getRegions } from 'Actions/center_action'
import { store } from './store.index'
import WebFont from 'webfontloader'
import {
  captureEmbedTrackingFlags,
  getFbTrackingData,
  getFontName,
  inIframe,
  setAnalyticsConsent
} from './utils/helper'
import Footer from './components/Footer'
import Loader from 'Components/Loader'
import UnderService from 'Pages/UnderService'
import NavBar from './components/NavBar'
import useQuery from './custom-hooks/useQuery'
import KenkoLoader from './components/KenkoLoader'

const GlobalStyle = createGlobalStyle`
  body {
    color: ${props => props.theme.color.brand};
    background-color: ${props => props.theme.color.bg};
    font-family: ${props => props.theme.fontFamily};
    font-size: 1rem;
    line-height: 1.25em;
    padding: 0 24px;

    @media (max-width: ${props => props.theme.breakpoint.md}px) {
      padding: 0 12px;
    }
  }
  h1,h2,h3,h4,h5,p,span,section,article,ul,li {
    margin:0;
    padding:0;
  }
`

const App = ({ regions }) => {
  const dispatch = useDispatch()
  const thunkDispatch = useThunkDispatch()
  const [theme, setTheme] = useState(null)
  const [wafLoading, setWafLoading] = useState(true)

  const renderServicePage = useSelector(
    store => store.websiteReducer.renderServicePage
  )
  const apps = useSelector(
    store => store.centerreducer.regionsDetails?.client?.apps
  )

  const centers = useSelector(store => store.centerreducer)

  useEffect(() => {
    const getWafToken = async () => {
      let interval = setInterval(async () => {
        try {
          if (typeof window.AwsWafIntegration !== 'undefined') {
            if (!window.AwsWafIntegration.hasToken()) {
              const token = await window.AwsWafIntegration.getToken()
              localStorage.setItem('aws-waf-token', token)
            }
            clearInterval(interval)
            setWafLoading(false)
          }
        } catch (err) {
          console.log('wftkn ~ err:', err?.message ?? err)
        } finally {
          setWafLoading(false)
        }
      }, 10)
    }
    getWafToken()
  }, [])

  useEffect(() => {
    if (!wafLoading) {
      const url_prefix = getUrlParams()
      dispatch(setUrlPrefix(url_prefix))
      thunkDispatch(getRegions({ url_prefix }))
    }
  }, [wafLoading])

  const initializeGA = trackingID => {
    if (trackingID) {
      ReactGA.initialize(trackingID, {
        gaOptions: {
          cookieFlags: 'samesite=none;secure'
        },
        gtagOptions: {
          cookie_flags: 'samesite=none;secure',
          linker: { accept_incoming: true }
        }
      })
    }
  }

  const initializeMP = trackingID => {
    if (trackingID) {
      const options = {
        // Disable automatic InitiateCheckout detection; embed fires it explicitly
        // with ISO currency + value when checkout starts.
        autoConfig: false,
        debug: false
      }
      // Advanced matching: share external_id with server-hashed CAPI value
      const { externalId } = getFbTrackingData()
      const advancedMatching = externalId ? { external_id: externalId } : null
      ReactPixel.init(trackingID, advancedMatching, options)
      ReactPixel.pageView()
    }
  }

  useEffect(() => {
    captureEmbedTrackingFlags()
  }, [])

  useEffect(() => {
    // Skip analytics/tracker init on auth-only routes. Loading fbevents.js on
    // /magic-login triggers Safari's tracker protection warning and serves no
    // purpose mid-auth. The route can appear either as the iframe pathname
    // (when loaded via the embed script, which rewrites the parent hash into
    // the iframe src) or as a hash route (when the app is opened standalone).
    const isAuthRoute =
      typeof window !== 'undefined' &&
      ((window.location.pathname || '').startsWith('/magic-login') ||
        (window.location.hash || '').startsWith('#/magic-login'))

    if (!isAuthRoute && apps && Object.keys(apps).length > 0) {
      if (apps.google_analytics)
        initializeGA(apps.google_analytics?.tracking_id)
      if (apps.meta_pixel) initializeMP(apps.meta_pixel?.tracking_id)
    }
  }, [apps])

  useEffect(() => {
    if (!inIframe()) return

    // Block analytics until the parent explicitly grants consent
    setAnalyticsConsent(false)

    const handleConsentMessage = e => {
      try {
        const data = typeof e.data === 'object' ? e.data : JSON.parse(e.data)
        if (data?.type === 'SetConsentState') {
          setAnalyticsConsent(!!data.granted)
        }
      } catch {
        // Ignore non-JSON or unrelated messages
      }
    }

    window.addEventListener('message', handleConsentMessage)
    return () => window.removeEventListener('message', handleConsentMessage)
  }, [])

  useEffect(() => {
    if (centers) {
      const centersById = centers.centersById
      const regionsById = centers.regionsById
      const regionId = parseInt(localStorage.getItem('region_id') || '0')
      const locationId = parseInt(
        localStorage.getItem('selected_center_id') || '0'
      )

      if (regionsById && regionId) {
        const regionName = regionsById[regionId]?.name
        localStorage.setItem('regionName', regionName)
      }
      if (centersById && locationId) {
        const locationName = centersById[locationId]?.branch
        localStorage.setItem('locationName', locationName)
      }
    }
  }, [centers])

  useClientTheme(theme)

  useEffect(() => {
    if (regions && regions.length > 0) {
      moment.tz.setDefault(regions[0].timezone)
      let {
        brand_color,
        background_color,
        text_color,
        font_family,
        waitlist_text_color
      } = regions[0].centers[0].settings.general
      const fontFamily = getFontName(font_family)
      WebFont.load({
        google: {
          families: [`${fontFamily.loaderName}:400,700`]
        }
      })
      setTheme(
        getTheme({
          brand: brand_color,
          bg: background_color,
          primaryText: text_color,
          fontFamily: fontFamily.applyName,
          waitlistText: waitlist_text_color
        })
      )
    }
  }, [regions])

  if (renderServicePage)
    return (
      <>
        <UnderService />
        <Footer />
      </>
    )

  if (wafLoading || theme === null || regions?.length === 0) {
    return (
      <div
        style={{
          minHeight: '50vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <KenkoLoader size={100} />
      </div>
    )
  }

  return (
    <ThemeProvider theme={theme}>
      <Fragment>
        <GlobalStyle />
        <BrowserRouter>
          <NavBar />
          <Routes />
        </BrowserRouter>
        <Footer theme={theme} />
      </Fragment>
    </ThemeProvider>
  )
}

const mapStateToProps = state => {
  return {
    regions: state.centerreducer.regionsDetails
      ? state.centerreducer.regionsDetails.regions
      : null
  }
}

export default connect(mapStateToProps, null)(App)
